import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, isEmpty, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAlternateEmail,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCall,
  MdOutlineCheckCircle,
  MdOutlineHealthAndSafety,
  MdOutlineSubtitles,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  GENDER,
  RELATIONSHIP,
  SOCIAL_REGIMES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Partner = ({
  formProps,
  values,
  setFieldValue,
  setPercent,
  isFieldRequired,
  errors,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = p("DETAIL.COUNTRIES", { returnObjects: true });
  const birthDate = get(
    initialValues,
    "spouse.identity.user.birthDate",
    undefined,
  );

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.SPOUSE,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Input
                placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                label={SUBSCRIPTION.FORM.LAST_NAME.LABEL}
                icon={<MdOutlineBadge size={16} />}
                defaultValue={get(
                  initialValues,
                  "spouse.identity.user.lastname",
                )}
                errors={errors["spouse.identity.user.lastname"]}
                touched={!isEmpty(errors["spouse.identity.user.lastname"])}
                onChange={({ target: { value } }) => {
                  set(initialValues, "spouse.identity.user.lastname", value);
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired("spouse.identity.user.lastname")}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                placeholder={SUBSCRIPTION.FORM.MARITAL_NAME.PLACEHOLDER}
                label={SUBSCRIPTION.FORM.MARITAL_NAME.LABEL}
                icon={<MdOutlineBadge size={16} />}
                defaultValue={get(
                  initialValues,
                  "spouse.identity.user.maritalName",
                )}
                errors={errors["spouse.identity.user.maritalName"]}
                touched={!isEmpty(errors["spouse.identity.user.maritalName"])}
                onChange={({ target: { value } }) => {
                  set(initialValues, "spouse.identity.user.maritalName", value);
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired("spouse.identity.user.maritalName")}
                optional={!isFieldRequired("spouse.identity.user.maritalName")}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                label={SUBSCRIPTION.FORM.FIRST_NAME.LABEL}
                icon={<MdOutlineBadge size={16} />}
                placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                defaultValue={get(
                  initialValues,
                  "spouse.identity.user.firstname",
                )}
                errors={errors["spouse.identity.user.firstname"]}
                touched={!isEmpty(errors["spouse.identity.user.firstname"])}
                onChange={({ target: { value } }) => {
                  set(initialValues, "spouse.identity.user.firstname", value);
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired("spouse.identity.user.firstname")}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                icon={<MdOutlineCake size={16} />}
                placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                defaultValue={birthDate && dayjs(birthDate)}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                disabled
                required={isFieldRequired("spouse.identity.user.birthDate")}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Select
                icon={<MdOutlineHealthAndSafety size={16} />}
                options={Object.entries(SOCIAL_REGIMES).map(([key, value]) => ({
                  value,
                  label:
                    SUBSCRIPTION.FORM.MANDATORY_SCHEME.MUTUAL_MOBILITY_OPTIONS[
                      key
                    ],
                }))}
                label={SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL}
                placeholder={SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER}
                defaultValue={get(
                  initialValues,
                  "spouse.identity.socialRegime",
                )}
                errors={errors["spouse.identity.socialRegime"]}
                touched={!isEmpty(errors["spouse.identity.socialRegime"])}
                onChange={(value) => {
                  set(initialValues, "spouse.identity.socialRegime", value);
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired("spouse.identity.socialRegime")}
                disabled
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                label={SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL}
                icon={<MdOutlineSubtitles size={16} />}
                placeholder={
                  SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                }
                trim={true}
                defaultValue={get(
                  initialValues,
                  "spouse.identity.socialSecurityNumber",
                )}
                errors={errors["spouse.identity.socialSecurityNumber"]}
                touched={
                  !isEmpty(errors["spouse.identity.socialSecurityNumber"])
                }
                onChange={({ target: { value } }) => {
                  set(
                    initialValues,
                    "spouse.identity.socialSecurityNumber",
                    value,
                  );
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired(
                  "spouse.identity.socialSecurityNumber",
                )}
                optional={
                  !isFieldRequired("spouse.identity.socialSecurityNumber")
                }
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                type="radio"
                variant="group"
                label={SUBSCRIPTION.FORM.GENDER.LABEL}
                values={[
                  {
                    label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                    value: GENDER.MALE,
                  },
                  {
                    label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                    value: GENDER.FEMALE,
                  },
                ]}
                icon={<MdOutlineBadge size={16} />}
                value={get(initialValues, "spouse.identity.user.gender")}
                errors={errors["spouse.identity.user.gender"]}
                touched={!isEmpty(errors["spouse.identity.user.gender"])}
                onChange={(value) => {
                  set(initialValues, "spouse.identity.user.gender", value);
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired("spouse.identity.user.gender")}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                optional={SUBSCRIPTION.FORM.OPTIONAL}
                label={SUBSCRIPTION.FORM.MOBILE.LABEL}
                icon={<MdOutlineCall size={16} />}
                placeholder={SUBSCRIPTION.FORM.MOBILE.PLACEHOLDER}
                defaultValue={get(
                  initialValues,
                  `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                )}
                errors={
                  errors[
                    `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`
                  ]
                }
                touched={
                  !isEmpty(
                    errors[
                      `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`
                    ],
                  )
                }
                onChange={({ target: { value } }) => {
                  set(
                    initialValues,
                    `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                    value,
                  );
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired(
                  `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                )}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                optional={SUBSCRIPTION.FORM.OPTIONAL}
                label={SUBSCRIPTION.FORM.EMAIL.LABEL}
                icon={<MdOutlineAlternateEmail size={16} />}
                placeholder={SUBSCRIPTION.FORM.EMAIL.PLACEHOLDER}
                defaultValue={get(
                  initialValues,
                  `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                )}
                errors={
                  errors[
                    `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`
                  ]
                }
                touched={
                  !isEmpty(
                    errors[
                      `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`
                    ],
                  )
                }
                onChange={({ target: { value } }) => {
                  set(
                    initialValues,
                    `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                    value,
                  );
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired(
                  `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                )}
              />
            </Col>
            <Col lg={24} xs={24}>
              <Input
                type="switch"
                label={SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL}
                icon={<MdOutlineCheckCircle size={16} />}
                defaultChecked={
                  !get(initialValues, `spouse.identity.fields.teletransmission`)
                }
                errors={errors[`spouse.identity.fields.teletransmission`]}
                touched={
                  !isEmpty(errors[`spouse.identity.fields.teletransmission`])
                }
                onChange={(value) => {
                  set(
                    initialValues,
                    `spouse.identity.fields.teletransmission`,
                    !value,
                  );
                }}
              />
            </Col>
          </Row>
        </Form>
      </div>
    )
  );
};

export default Partner;
